import { QueryClientProvider, type QueryClient } from '@tanstack/react-query';
import { createStore, Provider as JotaiProvider } from 'jotai';
import { queryClientAtom } from 'jotai-tanstack-query';
import { useHydrateAtoms } from 'jotai/utils';
import { useState } from 'react';
import { appStore } from '@/atoms/store';
import type { ReactNode } from 'react';

// Hydrate jotai-tanstack-query with the same QueryClient that
// QueryClientProvider (and each page's HydrationBoundary) uses, so
// atomWithQuery/atomWithInfiniteQuery atoms read SSR-prefetched data.
const HydrateQueryClient = ({
  children,
  queryClient
}: {
  children: ReactNode;
  queryClient: QueryClient;
}) => {
  useHydrateAtoms([[queryClientAtom, queryClient]]);
  return children;
};

/**
 * Request-scoped state wiring: the Jotai store and the TanStack QueryClient.
 *
 * Everything that hydrates per-request server state into atoms
 * (`queryClientAtom` above, auth atoms via `AuthHydrationProvider`) relies on
 * `useHydrateAtoms`, which hydrates ONCE per store. The store must therefore
 * be request-scoped on the server: a module-level singleton store means every
 * request after the first on a warm serverless instance silently keeps the
 * previous request's QueryClient/auth state, so listing pages SSR their
 * loading skeletons instead of the dehydrated results (no-JS/Googlebot
 * regression, July 2026). See AppStateProvider.ssr.test.tsx.
 */
const getJotaiStore = () => {
  // Server: a fresh store per request, so per-request hydration always runs
  // and one request's state can never leak into another's SSR HTML.
  if (typeof window === 'undefined') {
    return createStore();
  }
  // Browser: the shared appStore singleton, so non-React code that calls
  // appStore.set()/get() stays in sync with mounted components.
  return appStore;
};
export const AppStateProvider = ({
  children,
  queryClient
}: {
  children: ReactNode;
  queryClient: QueryClient;
}) => {
  // useState lazy-initialiser: one store per App mount — per request on the
  // server, once per browser session on the client (same pattern as the
  // QueryClient in _app.page.tsx).
  const [store] = useState(getJotaiStore);
  return <JotaiProvider store={store} data-sentry-element="JotaiProvider" data-sentry-component="AppStateProvider" data-sentry-source-file="AppStateProvider.tsx">
      <QueryClientProvider client={queryClient} data-sentry-element="QueryClientProvider" data-sentry-source-file="AppStateProvider.tsx">
        <HydrateQueryClient queryClient={queryClient} data-sentry-element="HydrateQueryClient" data-sentry-source-file="AppStateProvider.tsx">
          {children}
        </HydrateQueryClient>
      </QueryClientProvider>
    </JotaiProvider>;
};